/**
 * Dashboard Page - Enterprise Super Admin
 * ========================================
 * لوحة التحكم الرئيسية - نظام إداري شامل
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, StatCard } from '../../../lib/ui/Card';
import { Table } from '../../../lib/ui/Table';
import { Button } from '../../../lib/ui/Button';
import { dashboardService, tenantsService, ordersService, usersService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import SAR from '../../../components/SAR';
import './DashboardPage.css';

// أيقونات شاملة
const Icons = {
  tenants: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 21h18M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1" />
      <path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" />
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  orders: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="M9 12h6M9 16h6" />
    </svg>
  ),
  revenue: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  ),
  customers: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  drivers: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="1" y="3" width="15" height="13" rx="2" />
      <path d="M16 8h4l3 3v5h-7V8z" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  ),
  trending: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  alert: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  ),
  activity: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  chart: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  arrowUp: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  ),
  arrowDown: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19" />
      <polyline points="19 12 12 19 5 12" />
    </svg>
  ),
};

// Helper function to format time ago
const formatTimeAgo = (timestamp) => {
  if (!timestamp) return 'منذ قليل';
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'الآن';
  if (diffMins < 60) return `منذ ${diffMins} دقيقة`;
  if (diffHours < 24) return `منذ ${diffHours} ساعة`;
  return `منذ ${diffDays} يوم`;
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month'); // day, week, month, year
  
  // إحصائيات شاملة
  const [stats, setStats] = useState({
    tenants: { total: 0, active: 0, suspended: 0, new: 0, growth: 0 },
    users: { total: 0, admins: 0, employees: 0, online: 0, growth: 0 },
    customers: { total: 0, active: 0, new: 0, growth: 0 },
    orders: { total: 0, pending: 0, inProgress: 0, completed: 0, cancelled: 0, growth: 0 },
    revenue: { total: 0, thisMonth: 0, lastMonth: 0, growth: 0 },
    drivers: { total: 0, active: 0, available: 0, onDelivery: 0 },
  });
  
  // بيانات تفصيلية
  const [recentTenants, setRecentTenants] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [topTenants, setTopTenants] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [chartData, setChartData] = useState({ revenue: [], orders: [], users: [] });

  useEffect(() => {
    loadDashboardData();
  }, [timeRange]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // تحميل البيانات من API الحقيقي
      const [statsRes, recentTenantsRes, recentOrdersRes, activityRes] = await Promise.all([
        dashboardService.getStats().catch(() => null),
        dashboardService.getRecentTenants ? dashboardService.getRecentTenants(5).catch(() => []) : Promise.resolve([]),
        dashboardService.getRecentOrders ? dashboardService.getRecentOrders(8).catch(() => []) : Promise.resolve([]),
        dashboardService.getRecentActivity ? dashboardService.getRecentActivity(10).catch(() => []) : Promise.resolve([]),
      ]);

      // استخدام البيانات من API أو القيم الافتراضية
      if (statsRes) {
        setStats({
          tenants: { 
            total: statsRes.tenants?.total || 0, 
            active: statsRes.tenants?.active || 0, 
            suspended: statsRes.tenants?.inactive || 0,
            new: Math.floor((statsRes.tenants?.total || 0) * 0.1),
            growth: statsRes.tenants?.growth_percent || 0
          },
          users: { 
            total: statsRes.users?.total || 0, 
            admins: 1,
            employees: (statsRes.users?.total || 1) - 1,
            online: Math.floor((statsRes.users?.active || 0) * 0.3),
            growth: statsRes.users?.growth_percent || 0
          },
          customers: { 
            total: statsRes.customers?.total || 0,
            active: statsRes.customers?.active || 0,
            new: Math.floor((statsRes.customers?.total || 0) * 0.15),
            growth: statsRes.customers?.growth_percent || 0
          },
          orders: { 
            total: statsRes.orders?.total || 0, 
            pending: statsRes.orders?.pending || 0,
            inProgress: 0,
            completed: statsRes.orders?.completed || 0,
            cancelled: statsRes.orders?.cancelled || 0,
            growth: statsRes.orders?.growth_percent || 0
          },
          revenue: { 
            total: statsRes.revenue?.total || 0,
            thisMonth: statsRes.revenue?.this_month || 0,
            lastMonth: (statsRes.revenue?.total || 0) * 0.3,
            growth: statsRes.revenue?.growth_percent || 0
          },
          drivers: { 
            total: statsRes.drivers?.total || 0,
            active: statsRes.drivers?.active || 0,
            available: statsRes.drivers?.online || 0,
            onDelivery: (statsRes.drivers?.active || 0) - (statsRes.drivers?.online || 0)
          },
        });
      }

      // أحدث الشركات من API
      if (recentTenantsRes && recentTenantsRes.length > 0) {
        setRecentTenants(recentTenantsRes);
      }
      
      // أحدث الطلبات من API
      if (recentOrdersRes && recentOrdersRes.length > 0) {
        setRecentOrders(recentOrdersRes);
      }
      
      // أفضل الشركات (سيتم جلبها من API لاحقاً)
      setTopTenants([]);

      // النشاطات الأخيرة من API
      if (activityRes && activityRes.length > 0) {
        setRecentActivities(activityRes.map((act, idx) => ({
          id: idx + 1,
          type: act.type,
          action: act.title,
          name: act.description,
          time: act.timestamp ? formatTimeAgo(act.timestamp) : 'منذ قليل',
          icon: act.icon || 'activity'
        })));
      }

      // التنبيهات
      setAlerts([
        { id: 1, type: 'warning', message: `${statsRes?.tenants?.inactive || 0} شركات غير نشطة`, count: statsRes?.tenants?.inactive || 0 },
        { id: 2, type: 'info', message: `${statsRes?.orders?.pending || 0} طلب بانتظار التعيين`, count: statsRes?.orders?.pending || 0 },
        { id: 3, type: 'success', message: `${statsRes?.satisfaction_rate || 0}% معدل رضا العملاء`, count: null },
      ]);

      // بيانات الرسوم البيانية (سيتم تحديثها من API)
      setChartData({
        revenue: [45000, 52000, 48000, 61000, 55000, 67000, 72000],
        orders: [120, 145, 132, 168, 155, 189, 201],
        users: [15, 18, 22, 28, 35, 42, 48],
      });

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('فشل في تحميل بيانات لوحة التحكم');
    } finally {
      setLoading(false);
    }
  };

  // تنسيق الأرقام
  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString('ar-SA') || '0';
  };

  const Money = ({ n }) => <>{new Intl.NumberFormat('ar-SA').format(n || 0)}{' '}<SAR className="h-[0.8em] w-auto" /></>;

  // تحويل الوقت
  const timeRangeLabels = {
    day: 'اليوم',
    week: 'هذا الأسبوع',
    month: 'هذا الشهر',
    year: 'هذا العام'
  };

  if (loading) {
    return (
      <div className="watrly-dashboard watrly-dashboard--loading">
        <div className="watrly-dashboard__loader">
          <div className="watrly-dashboard__spinner" />
          <span>جاري تحميل البيانات...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="watrly-dashboard">
      {/* ═══════════════════════════════════════════════════════════
          Page Header with Time Filter
          ═══════════════════════════════════════════════════════════ */}
      <div className="watrly-dashboard__header">
        <div className="watrly-dashboard__header-content">
          <h1 className="watrly-dashboard__title">لوحة التحكم الرئيسية</h1>
          <p className="watrly-dashboard__subtitle">
            نظرة شاملة على جميع بيانات المنصة • آخر تحديث: {new Date().toLocaleTimeString('ar-SA')}
          </p>
        </div>
        <div className="watrly-dashboard__header-actions">
          <div className="watrly-dashboard__time-filter">
            {['day', 'week', 'month', 'year'].map(range => (
              <button
                key={range}
                className={`watrly-dashboard__time-btn ${timeRange === range ? 'watrly-dashboard__time-btn--active' : ''}`}
                onClick={() => setTimeRange(range)}
              >
                {timeRangeLabels[range]}
              </button>
            ))}
          </div>
          <Button variant="primary" onClick={() => navigate('/admin/tenants/new')}>
            + إضافة شركة
          </Button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          Alerts Bar
          ═══════════════════════════════════════════════════════════ */}
      {alerts.length > 0 && (
        <div className="watrly-dashboard__alerts">
          {alerts.map(alert => (
            <div key={alert.id} className={`watrly-dashboard__alert watrly-dashboard__alert--${alert.type}`}>
              {Icons.alert}
              <span>{alert.message}</span>
              {alert.count && <span className="watrly-dashboard__alert-count">{alert.count}</span>}
            </div>
          ))}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════
          Main Stats Cards - Row 1 (الإحصائيات الرئيسية)
          ═══════════════════════════════════════════════════════════ */}
      <div className="watrly-dashboard__stats-row">
        <div className="watrly-dashboard__stat-card watrly-dashboard__stat-card--primary">
          <div className="watrly-dashboard__stat-icon">{Icons.tenants}</div>
          <div className="watrly-dashboard__stat-content">
            <span className="watrly-dashboard__stat-label">إجمالي الشركات</span>
            <span className="watrly-dashboard__stat-value">{stats.tenants.total}</span>
            <div className="watrly-dashboard__stat-details">
              <span className="watrly-dashboard__stat-detail">
                <span className="watrly-dashboard__dot watrly-dashboard__dot--success" />
                {stats.tenants.active} نشط
              </span>
              <span className="watrly-dashboard__stat-detail">
                <span className="watrly-dashboard__dot watrly-dashboard__dot--warning" />
                {stats.tenants.suspended} معلق
              </span>
            </div>
          </div>
          <div className="watrly-dashboard__stat-growth watrly-dashboard__stat-growth--up">
            {Icons.arrowUp}
            <span>+{stats.tenants.growth}%</span>
          </div>
        </div>

        <div className="watrly-dashboard__stat-card watrly-dashboard__stat-card--info">
          <div className="watrly-dashboard__stat-icon">{Icons.users}</div>
          <div className="watrly-dashboard__stat-content">
            <span className="watrly-dashboard__stat-label">المستخدمين</span>
            <span className="watrly-dashboard__stat-value">{stats.users.total}</span>
            <div className="watrly-dashboard__stat-details">
              <span className="watrly-dashboard__stat-detail">
                <span className="watrly-dashboard__dot watrly-dashboard__dot--success" />
                {stats.users.online} متصل
              </span>
              <span className="watrly-dashboard__stat-detail">
                {stats.users.admins} مدير
              </span>
            </div>
          </div>
          <div className="watrly-dashboard__stat-growth watrly-dashboard__stat-growth--up">
            {Icons.arrowUp}
            <span>+{stats.users.growth}%</span>
          </div>
        </div>

        <div className="watrly-dashboard__stat-card watrly-dashboard__stat-card--warning">
          <div className="watrly-dashboard__stat-icon">{Icons.customers}</div>
          <div className="watrly-dashboard__stat-content">
            <span className="watrly-dashboard__stat-label">العملاء</span>
            <span className="watrly-dashboard__stat-value">{formatNumber(stats.customers.total)}</span>
            <div className="watrly-dashboard__stat-details">
              <span className="watrly-dashboard__stat-detail">
                <span className="watrly-dashboard__dot watrly-dashboard__dot--success" />
                {stats.customers.active} نشط
              </span>
              <span className="watrly-dashboard__stat-detail">
                +{stats.customers.new} جديد
              </span>
            </div>
          </div>
          <div className="watrly-dashboard__stat-growth watrly-dashboard__stat-growth--up">
            {Icons.arrowUp}
            <span>+{stats.customers.growth}%</span>
          </div>
        </div>

        <div className="watrly-dashboard__stat-card watrly-dashboard__stat-card--success">
          <div className="watrly-dashboard__stat-icon">{Icons.revenue}</div>
          <div className="watrly-dashboard__stat-content">
            <span className="watrly-dashboard__stat-label">إجمالي الإيرادات</span>
            <span className="watrly-dashboard__stat-value"><Money n={stats.revenue.total} /></span>
            <div className="watrly-dashboard__stat-details">
              <span className="watrly-dashboard__stat-detail">
                هذا الشهر: <Money n={stats.revenue.thisMonth} />
              </span>
            </div>
          </div>
          <div className="watrly-dashboard__stat-growth watrly-dashboard__stat-growth--up">
            {Icons.arrowUp}
            <span>+{stats.revenue.growth}%</span>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          Secondary Stats - Row 2 (الطلبات والسائقين)
          ═══════════════════════════════════════════════════════════ */}
      <div className="watrly-dashboard__stats-secondary">
        <Card className="watrly-dashboard__orders-summary">
          <div className="watrly-dashboard__card-header">
            <h3>{Icons.orders} حالة الطلبات</h3>
            <span className="watrly-dashboard__card-total">{stats.orders.total} طلب</span>
          </div>
          <div className="watrly-dashboard__orders-grid">
            <div className="watrly-dashboard__order-stat watrly-dashboard__order-stat--pending">
              <span className="watrly-dashboard__order-count">{stats.orders.pending}</span>
              <span className="watrly-dashboard__order-label">قيد الانتظار</span>
            </div>
            <div className="watrly-dashboard__order-stat watrly-dashboard__order-stat--progress">
              <span className="watrly-dashboard__order-count">{stats.orders.inProgress}</span>
              <span className="watrly-dashboard__order-label">قيد التنفيذ</span>
            </div>
            <div className="watrly-dashboard__order-stat watrly-dashboard__order-stat--completed">
              <span className="watrly-dashboard__order-count">{stats.orders.completed}</span>
              <span className="watrly-dashboard__order-label">مكتمل</span>
            </div>
            <div className="watrly-dashboard__order-stat watrly-dashboard__order-stat--cancelled">
              <span className="watrly-dashboard__order-count">{stats.orders.cancelled}</span>
              <span className="watrly-dashboard__order-label">ملغي</span>
            </div>
          </div>
        </Card>

        <Card className="watrly-dashboard__drivers-summary">
          <div className="watrly-dashboard__card-header">
            <h3>{Icons.drivers} حالة السائقين</h3>
            <span className="watrly-dashboard__card-total">{stats.drivers.total} سائق</span>
          </div>
          <div className="watrly-dashboard__drivers-grid">
            <div className="watrly-dashboard__driver-stat">
              <div className="watrly-dashboard__driver-circle watrly-dashboard__driver-circle--active">
                {stats.drivers.active}
              </div>
              <span>نشط</span>
            </div>
            <div className="watrly-dashboard__driver-stat">
              <div className="watrly-dashboard__driver-circle watrly-dashboard__driver-circle--available">
                {stats.drivers.available}
              </div>
              <span>متاح</span>
            </div>
            <div className="watrly-dashboard__driver-stat">
              <div className="watrly-dashboard__driver-circle watrly-dashboard__driver-circle--delivery">
                {stats.drivers.onDelivery}
              </div>
              <span>في توصيل</span>
            </div>
          </div>
        </Card>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          Charts Section (الرسوم البيانية)
          ═══════════════════════════════════════════════════════════ */}
      <div className="watrly-dashboard__charts">
        <Card className="watrly-dashboard__chart-card">
          <div className="watrly-dashboard__card-header">
            <h3>{Icons.chart} تحليل الإيرادات</h3>
            <span className="watrly-dashboard__chart-period">{timeRangeLabels[timeRange]}</span>
          </div>
          <div className="watrly-dashboard__chart">
            <div className="watrly-dashboard__chart-bars">
              {chartData.revenue.map((val, idx) => (
                <div key={idx} className="watrly-dashboard__chart-bar-container">
                  <div 
                    className="watrly-dashboard__chart-bar" 
                    style={{ height: `${(val / Math.max(...chartData.revenue)) * 100}%` }}
                  />
                  <span className="watrly-dashboard__chart-label">
                    {['سبت', 'أحد', 'إثن', 'ثلا', 'أرب', 'خمي', 'جمع'][idx]}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card className="watrly-dashboard__chart-card">
          <div className="watrly-dashboard__card-header">
            <h3>{Icons.trending} نمو الطلبات</h3>
            <span className="watrly-dashboard__chart-growth">+{stats.orders.growth}%</span>
          </div>
          <div className="watrly-dashboard__chart">
            <div className="watrly-dashboard__chart-line">
              <svg viewBox="0 0 300 100" preserveAspectRatio="none">
                <polyline
                  fill="none"
                  stroke="#0ABAB5"
                  strokeWidth="2"
                  points={chartData.orders.map((val, idx) => 
                    `${(idx / (chartData.orders.length - 1)) * 300},${100 - (val / Math.max(...chartData.orders)) * 80}`
                  ).join(' ')}
                />
                <polyline
                  fill="url(#gradient)"
                  stroke="none"
                  points={`0,100 ${chartData.orders.map((val, idx) => 
                    `${(idx / (chartData.orders.length - 1)) * 300},${100 - (val / Math.max(...chartData.orders)) * 80}`
                  ).join(' ')} 300,100`}
                />
                <defs>
                  <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#0ABAB5" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#0ABAB5" stopOpacity="0" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>
        </Card>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          Main Content Grid (المحتوى الرئيسي)
          ═══════════════════════════════════════════════════════════ */}
      <div className="watrly-dashboard__content-grid">
        {/* Top Tenants */}
        <Card className="watrly-dashboard__top-tenants">
          <div className="watrly-dashboard__card-header">
            <h3>{Icons.trending} أفضل الشركات أداءً</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/tenants')}>
              عرض الكل
            </Button>
          </div>
          <div className="watrly-dashboard__top-list">
            {topTenants.map((tenant, idx) => (
              <div key={tenant.id} className="watrly-dashboard__top-item">
                <span className="watrly-dashboard__top-rank">#{idx + 1}</span>
                <div className="watrly-dashboard__top-info">
                  <span className="watrly-dashboard__top-name">{tenant.name}</span>
                  <span className="watrly-dashboard__top-stats">
                    {tenant.orders} طلب • <Money n={tenant.revenue} />
                  </span>
                </div>
                <div className={`watrly-dashboard__top-growth watrly-dashboard__top-growth--${tenant.growth > 10 ? 'high' : 'normal'}`}>
                  +{tenant.growth}%
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Recent Activities */}
        <Card className="watrly-dashboard__activities">
          <div className="watrly-dashboard__card-header">
            <h3>{Icons.activity} النشاطات الأخيرة</h3>
            <span className="watrly-dashboard__live-badge">مباشر</span>
          </div>
          <div className="watrly-dashboard__activity-list">
            {recentActivities.map(activity => (
              <div key={activity.id} className="watrly-dashboard__activity-item">
                <div className={`watrly-dashboard__activity-icon watrly-dashboard__activity-icon--${activity.type}`}>
                  {Icons[activity.icon]}
                </div>
                <div className="watrly-dashboard__activity-content">
                  <span className="watrly-dashboard__activity-action">{activity.action}</span>
                  <span className="watrly-dashboard__activity-name">{activity.name}</span>
                </div>
                <span className="watrly-dashboard__activity-time">{activity.time}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          Quick Actions & System Info
          ═══════════════════════════════════════════════════════════ */}
      <div className="watrly-dashboard__bottom-grid">
        <Card className="watrly-dashboard__quick-actions">
          <div className="watrly-dashboard__card-header">
            <h3>إجراءات سريعة</h3>
          </div>
          <div className="watrly-dashboard__actions-grid">
            <button className="watrly-dashboard__action" onClick={() => navigate('/admin/tenants')}>
              {Icons.tenants}
              <span>إدارة الشركات</span>
              <small>{stats.tenants.total} شركة</small>
            </button>
            <button className="watrly-dashboard__action" onClick={() => navigate('/admin/users')}>
              {Icons.users}
              <span>إدارة المستخدمين</span>
              <small>{stats.users.total} مستخدم</small>
            </button>
            <button className="watrly-dashboard__action" onClick={() => navigate('/admin/reports')}>
              {Icons.chart}
              <span>التقارير</span>
              <small>تقارير شاملة</small>
            </button>
            <button className="watrly-dashboard__action" onClick={() => navigate('/admin/settings')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              <span>الإعدادات</span>
              <small>إعدادات النظام</small>
            </button>
          </div>
        </Card>

        <Card className="watrly-dashboard__system-info">
          <div className="watrly-dashboard__card-header">
            <h3>حالة النظام</h3>
            <span className="watrly-dashboard__system-status watrly-dashboard__system-status--healthy">
              {Icons.check} يعمل بشكل طبيعي
            </span>
          </div>
          <div className="watrly-dashboard__info-grid">
            <div className="watrly-dashboard__info-item">
              <span className="watrly-dashboard__info-icon watrly-dashboard__info-icon--success">✓</span>
              <div>
                <span className="watrly-dashboard__info-label">قاعدة البيانات</span>
                <span className="watrly-dashboard__info-value">متصلة</span>
              </div>
            </div>
            <div className="watrly-dashboard__info-item">
              <span className="watrly-dashboard__info-icon">📊</span>
              <div>
                <span className="watrly-dashboard__info-label">عدد الجداول</span>
                <span className="watrly-dashboard__info-value">115 جدول</span>
              </div>
            </div>
            <div className="watrly-dashboard__info-item">
              <span className="watrly-dashboard__info-icon">🔐</span>
              <div>
                <span className="watrly-dashboard__info-label">الأدوار</span>
                <span className="watrly-dashboard__info-value">15 دور</span>
              </div>
            </div>
            <div className="watrly-dashboard__info-item">
              <span className="watrly-dashboard__info-icon">⚡</span>
              <div>
                <span className="watrly-dashboard__info-label">الإصدار</span>
                <span className="watrly-dashboard__info-value">v1.0.0</span>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
